import { Box } from '@chakra-ui/react';
import {
  Line,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  ComposedChart,
} from 'recharts';
import { KeyMetricChartDataPoint } from 'src/components/organisms/KeyMetricChart';
import {
  useAxisStyles,
  useChartColors,
  useChartDataColors,
} from 'src/hooks/colors/useChartColors';
import { TooltipContent } from '../../atoms/tooltips/TooltipContent';

export interface KeyMetricLineGraphProps {
  dataKeys: { xAxis: string; data: string };
  data: KeyMetricChartDataPoint[];
  xAxisFormatter?: (value: number | string) => string;
  yAxisFormatter?: (value: number) => string;
  benchmarkEnabled: boolean;
}

export default function KeyMetricLineGraph({
  dataKeys,
  data,
  xAxisFormatter = (value: number | string) => value.toString(),
  yAxisFormatter = (value: number) => value.toString(),
  benchmarkEnabled,
}: KeyMetricLineGraphProps) {
  const axisStyles = useAxisStyles();

  const { chartDataColors } = useChartDataColors();

  const { benchmarkLineColor } = useChartColors();

  return (
    <Box w="100%" h="100%">
      <ResponsiveContainer>
        <ComposedChart data={data}>
          <Tooltip
            wrapperStyle={{ zIndex: 10 }}
            offset={25}
            allowEscapeViewBox={{ x: true, y: true }}
            content={
              <TooltipContent
                valueFormatter={yAxisFormatter}
                labelFormatter={xAxisFormatter}
              />
            }
          />
          <defs>
            <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
              <stop stopColor="#27AE60" stopOpacity="0.7" />
              <stop offset="1" stopColor="#27AE60" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <XAxis
            dataKey={dataKeys.xAxis}
            minTickGap={20}
            tickFormatter={xAxisFormatter}
            {...axisStyles}
            tickLine={false}
            axisLine={false}
          />
          <Area
            key={dataKeys.data}
            dataKey={dataKeys.data}
            connectNulls
            isAnimationActive={false}
            stroke={chartDataColors[0]}
            dot={false}
            fill="url(#colorPv)"
            activeDot={{
              stroke: chartDataColors[0],
              fill: chartDataColors[0],
              strokeOpacity: 0.5,
              strokeWidth: 4,
              r: 6,
            }}
            strokeWidth={2}
          />
          {benchmarkEnabled && (
            <Line
              connectNulls
              dataKey={'benchmark'}
              isAnimationActive={false}
              stroke={benchmarkLineColor}
              strokeWidth={2}
              dot={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
